<template>
  <div>
    <Loader v-if="isLoading" style="position: absolute"></Loader>
    <div v-if="customer" class="ui form">
      <h1 class="org-settings-heading">Organization Settings</h1>
      <SrpTabs v-model:activeTab="activeTab" tabsFillWidth>
        <template #DestinationInfo-header="{ active }">
          <TabHeader :is-active="active" title="Destination Info" description="Basic info, site, and handles" icon="map-pin_1-5" />
        </template>
        <template #DestinationInfo>
          <div class="ui stackable grid">
            <div class="nine wide column">
              <div class="field">
                <label class="cms-section-title">Name</label>
                <p class="cms">The destination name you would like displayed</p>
                <input type="text" v-model="customer.title" />
              </div>

              <div v-if="!isHotel" class="field">
                <label class="cms-section-title">Description</label>
                <p class="cms">A short description of your destination</p>
                <textarea v-model="customer.description" autocapitalize="sentences"></textarea>
              </div>

              <div v-if="!isHotel" class="field">
                <label class="cms-section-title">Website</label>
                <p class="cms">The url of your website</p>
                <input type="text" v-model="customer.website" />
              </div>
              <div v-if="!isHotel" class="field">
                <label class="cms-section-title">Website Display Text</label>
                <p class="cms">(Optional) If you want to show this text instead of your website url</p>
                <input type="text" v-model="customer.websiteText" />
              </div>

              <div class="field">
                <label class="cms-section-title expandable-title">Social Media Handles</label>
                <p class="cms" style="margin-top: 0">Shown on your destination page and helps creators tag your handle during a creator visit.</p>
                <SocialMediaInputs :socialFields="customer" />
              </div>
            </div>

            <div class="four wide column right floated">
              <div class="ui form" style="margin-bottom: 22px">
                <div class="field">
                  <br />
                  <input @change="uploadLogo()" class="inputfile" type="file" id="logoImageInput" accept="image/*" />
                  <input type="text" v-model="customer.icon" style="display: none" />
                  <img :src="`${contentBaseUri}/cms/images/orgs/${customer.icon}`" class="ui image profile-image ui raised segment" />

                  <br />
                  <label for="logoImageInput">
                    <SrpButton size="small" fill="outlined" style="width: 100%">
                      <i class="ui upload icon"></i>
                      Upload Logo
                    </SrpButton>
                  </label>
                </div>
              </div>

              <div style="margin-bottom: 14px; border-bottom: 1px rgba(0, 0, 0, 0.1) solid"></div>

              <div style="margin-bottom: 17px; display: flex; align-items: center; color: rgba(91, 91, 91, 1); font-family: sans-serif">
                <GlobalAccentColorPicker style="margin: 0 10px 4px 0; z-index: 10" dropdownPosition="top" @change="newColorHex => (customer.accentColor = newColorHex)" />
              </div>
            </div>
          </div>
        </template>
        <template #CreatorVisits-header="{ active }">
          <TabHeader :is-active="active" title="Creator Visits" description="Guidance for all creator visits" icon="briefcase_1-5" />
        </template>
        <template #CreatorVisits>
          <div v-if="settings" class="creator-visits-tab">
            <div class="field" v-if="showCreatorDirectionsTemp">
              <label class="cms-section-title">Creator Visit Guidance</label>
              <p class="cms">This guidance will apply across every creator visit and will be visible to creators while applying and after accepting a creator visit.</p>
              <textarea
                v-model="settings.creatorDirection"
                autocapitalize="sentences"
                rows="4"
                style="height: 120px"
                placeholder="Ex. Stay within the county boundaries.
Or, stop by the visitor center to say hi. "
                :maxlength="CREATOR_DIRECTION_MAX_LENGTH"
              ></textarea>
              <p v-if="(settings.creatorDirection?.length ?? 0) >= CREATOR_DIRECTION_MAX_LENGTH" class="creator-direction-limit-warning">
                {{ CREATOR_DIRECTION_MAX_LENGTH.toLocaleString() }} character limit
              </p>
            </div>

            <div class="field">
              <label class="cms-section-title expandable-title">Boundary</label>
              <p class="cms" style="margin-top: 0">Show creators the boundary of your destination.</p>
              <!--NOTE: Similar UI in PaidCreatorFields-->
              <div class="travel-boundary-selections">
                <label class="checkbox-card" :class="{ active: settings.boundarySelectMode === 'draw' }" @click="setBoundaryMode('draw')">
                  <input type="radio" name="frequency" :checked="settings.boundarySelectMode === 'draw'" style="width: 13px; margin-right: 8px" />
                  <i class="pencil alternate icon"></i> Draw Boundary On Map
                </label>
                <label class="checkbox-card" :class="{ active: settings.boundarySelectMode === 'radius' }" @click="setBoundaryMode('radius')">
                  <input type="radio" name="frequency" :checked="settings.boundarySelectMode === 'radius'" style="width: 13px; margin-right: 8px" />
                  <i class="map marker alternate icon"></i> Radius around your Destination
                </label>
                <h3 v-if="boundaryValidationMessage" style="color: red; font-weight: bold; margin-top: 10px">{{ boundaryValidationMessage }}</h3>
              </div>
              <div class="map-top" v-if="settings.boundarySelectMode">
                <div>
                  <div v-if="settings.boundarySelectMode === 'radius'">
                    Everything within
                    <input type="number" min="10" max="400" step="10" v-model="settings.withinMiles" style="width: 70px; height: 25px; display: inline; margin-left: 0.5rem; margin-right: 0.5rem" />
                    miles of the map marker is included.
                  </div>
                  <h4 v-else-if="settings.boundarySelectMode === 'draw'">Click on the map to create your boundary!</h4>
                </div>
                <div style="display: flex; align-items: center">
                  <h4 v-if="settings.boundarySelectMode === 'radius'">The map marker is draggable</h4>
                </div>
              </div>
              <DrawingMap
                style="width: 100%; margin: 0px"
                mapAspectRatio="2/1"
                :lat="customer.latitude"
                :lng="customer.longitude"
                :inputGeoJson="settings.boundaryGeoJson"
                :circleRadiusInMiles="+(settings.withinMiles ?? 0)"
                :boundarySelectMode="settings.boundarySelectMode"
                :onlyAllowDrawingMode="false"
                :isCreatorFlow="false"
                @boundaryChanged="boundaryChanged"
                @markerManuallyMoved="markerManuallyMoved"
              />
            </div>
          </div>
        </template>
      </SrpTabs>

      <div class="community-page-admin__sticky-footer">
        <div class="community-page-admin__sticky-footer-actions">
          <SrpButton @click="save" class="community-page-admin__sticky-footer-button">Save</SrpButton>
          <SrpButton lang="en" hreflang="en" fill="outlined" @click="saveAndPreview">Preview</SrpButton>
          <span v-if="saveText" class="community-page-admin__sticky-footer-status">{{ saveText }}</span>
          <span v-if="imageUploading" class="community-page-admin__sticky-footer-status">Uploading...</span>
          <div v-if="errors.length" class="community-page-admin__sticky-footer-errors">
            <ul>
              <li v-for="error in errors" :key="error">
                <b>{{ error }}</b>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div style="margin-top: 50px; display: flex; justify-content: flex-end; width: 100%">
        <SuperUserActionPanel>
          <div>
            <router-link :to="{ name: 'EditCustomer', params: { id: customer.id } }" target="_blank" style="margin-right: 12px">
              <SrpButton color="gray" fill="outlined" size="tiny" style="opacity: 0.5">Backend edit</SrpButton>
            </router-link>
          </div>
        </SuperUserActionPanel>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import axios from "axios";
import Loader from "@components/Loader/Loader.vue";
import { BoundarySelectMode, Page, CustomerAdminEditV2Response, CustomerSettingsClientContract } from "@contracts/pages";
import DrawingMap from "./DrawingMap.vue";
import FileUtils from "@logic/FileUtils";
import SocialMediaInputs from "@components/SocialMediaInputs.vue";
import SocialFieldValidator from "@logic/SocialFieldValidator";
import { inject } from "vue";
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import { buildSquareAroundCenterAsGeoJson } from "@helpers/GeoSpatialHelper";
import { useHead } from "@unhead/vue";
import AdminContext from "@logic/AdminContext";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import SrpDetailsSummary from "@components/ui/SrpDetailsSummary.vue";
import GlobalAccentColorPicker from "@components/GlobalAccentColorPicker.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";
import { MetricSender } from "@/helpers/MetricSender";
import SuperUserActionPanel from "@components/SuperUserActionPanel.vue";
import SrpTabs from "@components/ui/SrpTabs.vue";
import TabHeader from "@views/Creator/CollabOpportunity/CollabOpportunityV2/Tabs/TabHeader.vue";

const CREATOR_DIRECTION_MAX_LENGTH = 2000;
const HASH_TO_TAB: Record<string, "DestinationInfo" | "CreatorVisits"> = { info: "DestinationInfo", visits: "CreatorVisits" };
const TAB_TO_HASH: Record<"DestinationInfo" | "CreatorVisits", string> = { DestinationInfo: "info", CreatorVisits: "visits" };

export default defineComponent({
  name: "CommunityPageAdmin",

  components: {
    InfoIconWithTooltip,
    GlobalAccentColorPicker,
    SrpButton,
    SrpModal,
    Loader,
    DrawingMap,
    SocialMediaInputs,
    IconEmbedded,
    SrpDetailsSummary,
    SuperUserActionPanel,
    SrpTabs,
    TabHeader,
  },

  data() {
    return {
      CREATOR_DIRECTION_MAX_LENGTH,
      globalLog: inject("globalLog") as any,
      globalRemoteLogger: inject("globalRemoteLogger") as any,

      activeTab: "DestinationInfo" as "DestinationInfo" | "CreatorVisits",
      title: "Organization Settings",
      customer: null as Page | null,
      settings: null as CustomerSettingsClientContract | null,
      isHotel: false,
      isLoading: true,
      showUnsavedChangesModal: false,
      saveText: null as string | null,
      errors: [] as Array<string>,
      boundaryValidationMessage: null,
      imageUploading: false,
      // @ts-ignore
      contentBaseUri: globalThis.Bootstrap.Config.contentCdnBaseUri,

      showCreatorDirectionsTemp: false,
    };
  },

  watch: {
    activeTab(newTab) {
      const hash = TAB_TO_HASH[newTab];
      const currentHash = this.$route.hash?.replace(/^#/, "") ?? "";
      if (currentHash !== hash) {
        this.$router.replace({
          name: this.$route.name ?? "CommunityPageAdmin",
          params: this.$route.params,
          query: this.$route.query,
          hash: `#${hash}`,
        });
      }
    },
    "$route.hash"(newHash) {
      const hashKey = newHash?.replace(/^#/, "") ?? "";
      const tab = HASH_TO_TAB[hashKey];
      if (tab && this.activeTab !== tab) {
        this.activeTab = tab;
      }
    },
  },

  async mounted() {
    useHead({ title: () => this.title ?? "" });

    const hashKey = this.$route.hash?.replace(/^#/, "") ?? "";
    const tab = HASH_TO_TAB[hashKey];
    if (tab) {
      this.activeTab = tab;
    }

    const id = this.$route.params.pageId as string;
    await AdminContext.setOrgInContextFromCustomerId(id);
    // @ts-ignore
    await this.loadData(id);
    AdminContext.setOrgInContextFromCustomer(this.customer);

    MetricSender.sendMetric("Cust-ViewDestSettings", null);
  },

  methods: {
    async loadData(identifier: string) {
      const uri = `${import.meta.env.VITE_API_URL}/pages/${identifier}/admin-edit/v2`;
      const { data } = await axios.get<CustomerAdminEditV2Response>(uri);
      this.customer = data.customer;
      this.settings = data.settings;
      this.isHotel = this.customer.type === "Hotel";
      this.isLoading = false;
      this.title += ` - ${this.customer.title}`;
    },
    async save(): Promise<boolean> {
      this.prepForSave();
      this.saveText = "";
      // Validate
      this.errors = [];
      this.boundaryValidationMessage = null;
      var socialFieldValidation = SocialFieldValidator.validateSocialFields(this.customer);
      if (!this.customer.title) {
        this.errors.push("Name is required");
        return false;
      }
      if (socialFieldValidation[0] === false) {
        this.errors.push(...socialFieldValidation[1]);
        return false;
      } else if (!this.settings.boundarySelectMode || this.settings.boundarySelectMode.trim().length === 0) {
        let message = "Select a boundary mode";
        this.errors.push(message);
        this.boundaryValidationMessage = message;
        return false;
      } else if (this.settings.boundarySelectMode === "draw" && (!this.settings.boundaryGeoJson || this.settings.boundaryGeoJson.trim().length === 0)) {
        let message = "Click the map to draw your boundary";
        this.errors.push(message);
        this.boundaryValidationMessage = message;
        return false;
      } else {
        // Save it!
        this.globalLog.info("Saving page!");
        const uri = `${import.meta.env.VITE_API_URL}/pages/${this.customer.id}/admin-edit/v2`;
        this.saveText = "Saving...";
        const { data } = await axios.put<CustomerAdminEditV2Response>(uri, { customer: this.customer, settings: this.settings });
        this.customer = data.customer;
        this.settings = data.settings;
        this.saveText = "Saved";
        return true;
      }
    },
    setBoundaryMode(mode: BoundarySelectMode) {
      this.settings.boundarySelectMode = mode;
      this.boundaryValidationMessage = null;
      this.errors = [];

      // Default the bounding box if they select draw mode
      // Note: We have similar logic on the creator side in onSelectBoundaryMode
      if (mode === "draw" && !this.settings.boundaryGeoJson) {
        if (this.customer.latitude && this.customer.longitude) {
          var geoJson = buildSquareAroundCenterAsGeoJson(this.customer.latitude, this.customer.longitude, this.settings.withinMiles ?? 10);
          this.settings.boundaryGeoJson = geoJson;
          this.globalRemoteLogger.info(`Destination: Defaulting square boundary with centerLat=${this.customer.latitude}, centerLng=${this.customer.longitude}: ${geoJson}`);
        }
      }
    },
    prepForSave() {
      // Ensure the website starts with http
      if (this.customer.website && !this.customer.website.startsWith("http")) {
        this.customer.website = "https://" + this.customer.website;
      }
      if (!this.customer.websiteText || this.customer.websiteText.trim().length === 0) {
        this.customer.websiteText = null;
      }
      SocialFieldValidator.fixSocialFields(this.customer);
    },
    boundaryChanged(geoJsonBoundaryString: string, areaInMiles: number) {
      this.settings.boundaryGeoJson = geoJsonBoundaryString;
      this.settings.boundaryAreaInMiles = areaInMiles;
    },
    markerManuallyMoved(lat: number, lng: number) {
      this.customer.latitude = lat;
      this.customer.longitude = lng;
    },
    async saveAndPreview() {
      if (await this.save()) {
        // Removed the old isDirty logic, just saving and navigating
        this.$router.push({ name: "Pages", params: { pageId: this.customer.uniqueName } });
      }
    },
    getGoogleMapLatLngLink(): string {
      return `https://www.google.com/maps/search/?api=1&query=${this.customer.latitude},${this.customer.longitude}`;
    },
    async uploadLogo() {
      // Note: Similar code in Profile.vue
      this.imageUploading = true;
      var uploadInput: any = document.getElementById("logoImageInput");
      if (uploadInput.files.length > 0) {
        var file = uploadInput.files[0];
        var error = FileUtils.isFileSupportedForUpload(file.name, file.type, file.size);
        if (error?.length > 0) {
          this.saveText = error;
        } else {
          var formData = new FormData();
          formData.append("file", file);
          try {
            var result = await axios.post(`${import.meta.env.VITE_API_URL}/images?type=CustomerIcon&flow=logo`, formData);
            this.saveText = null;
            this.customer.icon = result.data[0];
          } catch (error) {
            if (error.response?.status === 400) this.saveText = error.response.data;
            else this.saveText = "Error uploading image, please contact support@shrpa.com";
          }
        }

        // Note: Ideally should set the nav icon on partnerInfo in App.vue also...

        this.imageUploading = false;
      }
    },
  },
});
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/variables.scss";

.org-settings-heading {
  margin-bottom: 25px;
}

.expandable-title {
  margin-top: 5px !important;
  margin-left: 3px !important;
}

.map-top {
  padding: 8px 0;
  display: flex;
  justify-content: space-between;
}
.travel-boundary-selections {
  margin-top: 1rem;
}
.checkbox-card {
  width: auto !important;
  padding: 12px 16px;
  margin-right: 1rem !important;
  border: 1px solid rgba(255, 255, 255, 0.15) !important;
  border-radius: 4px;
  display: inline-flex !important;
  background-color: #eaeaea !important;
  cursor: pointer;

  label {
    cursor: pointer;
  }

  &.active {
    background-color: $brand-color-lighter !important;
    border-color: $brand-color-light !important;
    label {
      color: $brand-color !important;
    }
  }
}

.creator-visits-tab {
  min-height: 200px;
}

.creator-direction-limit-warning {
  font-size: 14px;
  margin-top: 4px;
  color: #945347;
  font-weight: 500;
}

// Sticky footer (patterned after StepCalendarPosts manage-buttons-floating-panel)
.community-page-admin__sticky-footer {
  position: sticky;
  inset: auto auto 0 0;
  margin-top: 25px;
  margin-bottom: 25px;
  padding-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  box-shadow: 0 -11px 16px 14px rgba(255, 255, 255, 1);
  background: rgba(255, 255, 255, 1);
  z-index: 10;
}

.community-page-admin__sticky-footer-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  width: 100%;
}

.community-page-admin__sticky-footer-button {
  margin-right: 0;
}

.community-page-admin__sticky-footer-status {
  margin-left: 8px;
}

.community-page-admin__sticky-footer-errors {
  width: 100%;
  color: firebrick;
  margin-top: 8px;

  ul {
    margin: 0;
    padding-left: 1.25rem;
  }
}

@media (max-width: $mobile-max-width) {
  .community-page-admin__sticky-footer {
    box-shadow: 0 -5px 20px 8px rgba(255, 255, 255, 1);
    margin-top: 20px;
    margin-bottom: 20px;
    padding: 12px;
  }

  .community-page-admin__sticky-footer-actions {
    gap: 10px;
  }
}
</style>
